import { useId } from 'react'
import { CLIENT_REGIONS, SHOWROOM_CITY } from '../../data/clientReach'
import { CLIENT_REACH_LABELS, MOBILE_CLIENT_REACH_LABELS, DESKTOP_REACH_VIEWBOX, MOBILE_REACH_VIEWBOX } from '../../data/clientReachLabels'
import { PAKISTAN_REGIONS, projectPakistan } from '../../data/pakistanMap'

const CITIES = CLIENT_REGIONS.flatMap((region) => region.cities.map((city) => ({ ...city, region: region.name })))
const LOCATIONS = [...CITIES, { ...SHOWROOM_CITY, region: 'Our showroom' }]

/* Every city stays lit and labelled. Northern cities are close neighbours,
   so callout lines make room for their names without moving the geographic
   pins. A separate label arrangement on small screens keeps the complete
   map visible, without filters, hover-only names or horizontal scrolling. */
export default function ClientReachMap() {
  const id = useId()

  const drawMap = (compact: boolean) => {
    const mapId = `${id}-${compact ? 'mobile' : 'desktop'}`
    const labels = compact ? MOBILE_CLIENT_REACH_LABELS : CLIENT_REACH_LABELS

    return (
      <svg
        viewBox={compact ? MOBILE_REACH_VIEWBOX : DESKTOP_REACH_VIEWBOX}
        role="img"
        aria-labelledby={`${mapId}-title ${mapId}-description`}
        className={compact ? 'block h-auto w-full sm:hidden' : 'hidden h-auto w-full sm:block'}
      >
        <title id={`${mapId}-title`}>Our client cities across Pakistan, AJK and Gilgit-Baltistan</title>
        <desc id={`${mapId}-description`}>
          {CLIENT_REGIONS.map((region) => `${region.name}: ${region.cities.map((city) => city.name).join(', ')}.`).join(' ')} Our showroom is in Islamabad. All locations are highlighted together.
        </desc>
        <defs>
          <pattern id={`${mapId}-grid`} width="32" height="32" patternUnits="userSpaceOnUse">
            <path d="M32 0H0V32" fill="none" stroke="var(--color-line)" strokeWidth="0.6" />
          </pattern>
          <pattern id={`${mapId}-dots`} width="8" height="8" patternUnits="userSpaceOnUse">
            <circle cx="4" cy="4" r="0.8" fill="var(--color-lime)" opacity="0.25" />
          </pattern>
        </defs>
        <rect x="-270" y="-20" width="1260" height="760" fill={`url(#${mapId}-grid)`} opacity="0.35" />

        {PAKISTAN_REGIONS.map((region) => (
          <g key={region.id}>
            <title>{region.name}</title>
            <path d={region.path} className="fill-lime-ink stroke-lime/60" strokeWidth="1" strokeLinejoin="round" />
            <path d={region.path} fill={`url(#${mapId}-dots)`} />
          </g>
        ))}

        {LOCATIONS.map((city) => {
          const point = projectPakistan(city.longitude, city.latitude)
          const label = labels[city.name]
          const endX = label.x + (label.anchor === 'end' ? 8 : -8)
          return (
            <path
              key={city.name}
              d={`M ${point.x} ${point.y} L ${label.elbowX} ${label.elbowY} L ${endX} ${label.y}`}
              fill="none"
              stroke="var(--color-lime)"
              strokeOpacity={compact ? 0.45 : 0.6}
              strokeWidth="0.8"
              vectorEffect="non-scaling-stroke"
            />
          )
        })}

        {LOCATIONS.map((city, index) => {
          const point = projectPakistan(city.longitude, city.latitude)
          const showroom = city.name === SHOWROOM_CITY.name
          return (
            <g key={city.name} data-client-city={city.name}>
              <title>{`${city.name} · ${city.region}`}</title>
              <circle cx={point.x} cy={point.y} r="13" fill="var(--color-lime)" opacity="0.12" />
              <circle
                cx={point.x} cy={point.y} r="10" fill="none" stroke="var(--color-lime)" strokeWidth="1.4"
                opacity="0.45" className="motion-safe:animate-city-ring" style={{ animationDelay: `${-index * 0.19}s` }}
              />
              {showroom ? (
                <path d={`M ${point.x} ${point.y - 5} L ${point.x + 5} ${point.y} L ${point.x} ${point.y + 5} L ${point.x - 5} ${point.y} Z`} fill="var(--color-platinum)" />
              ) : (
                <circle cx={point.x} cy={point.y} r={compact ? 6 : 4.5} fill="var(--color-lime)" stroke="var(--color-ink)" strokeWidth="1" />
              )}
            </g>
          )
        })}

        {LOCATIONS.map((city) => {
          const label = labels[city.name]
          return (
            <text
              key={city.name} x={label.x} y={label.y} textAnchor={label.anchor} dominantBaseline="middle"
              fill="var(--color-platinum)" stroke="var(--color-ink)" strokeWidth={compact ? 7 : 5} paintOrder="stroke"
              className={compact ? 'text-[32px] font-medium' : 'text-[13px] font-medium'}
            >
              {city.name}
            </text>
          )
        })}
      </svg>
    )
  }

  return (
    <div className="relative isolate overflow-hidden rounded-[24px] border border-lime/20 bg-ink shadow-card sm:rounded-[28px]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_65%_35%,var(--color-lime-ink),transparent_65%)] opacity-60" />
      <div className="relative mx-auto my-5 w-full max-w-[1120px] sm:my-0">
        {drawMap(false)}
        {drawMap(true)}
      </div>

      <div className="relative flex flex-wrap items-center justify-between gap-3 border-t border-line px-5 py-4 sm:px-9">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[10px] text-silver sm:text-xs">
          <span className="flex items-center gap-2"><span aria-hidden="true" className="size-2 rounded-full bg-lime" /> Our client cities</span>
          <span className="flex items-center gap-2"><span aria-hidden="true" className="text-platinum">◆</span> Islamabad showroom</span>
        </div>
      </div>
    </div>
  )
}
